import { onCleanup, onMount, type JSX } from 'solid-js';

export interface ReusableStylesheet {
  readonly key: string;
  readonly cssText: string;
  readonly sheet?: CSSStyleSheet;
}

type AdoptionMode = 'constructable' | 'fallback';

interface StylesheetOwnership {
  count: number;
  modes: Set<AdoptionMode>;
}

interface ReleasedStylesheetOwnership {
  readonly stylesheet: ReusableStylesheet;
  readonly modes: ReadonlySet<AdoptionMode>;
}

const adoptedFallbackKeys = new WeakMap<Document | ShadowRoot, Set<string>>();
const adoptedStylesheetOwnership = new WeakMap<
  Document | ShadowRoot,
  Map<ReusableStylesheet, StylesheetOwnership>
>();
const reusableStylesheetKeyProperty = '__trimbleReusableStylesheetKey';
const reusableStylesheetSignatureProperty =
  '__trimbleReusableStylesheetSignature';

interface KeyedCSSStyleSheet extends CSSStyleSheet {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __trimbleReusableStylesheetKey?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __trimbleReusableStylesheetSignature?: string;
}

export function createReusableStylesheet(
  key: string,
  cssText: string
): ReusableStylesheet {
  const sheet = createConstructableStylesheet(cssText);

  if (sheet) {
    markStylesheet(sheet, key);
    return { key, cssText, sheet };
  }

  return { key, cssText };
}

export function adoptReusableStylesheets(
  root: Document | ShadowRoot,
  stylesheets: readonly ReusableStylesheet[]
): void {
  const adoptions = stylesheets.map((stylesheet) => ({
    stylesheet,
    modes: getAdoptionModes(root, stylesheet),
  }));

  trackReusableStylesheets(root, adoptions);

  const constructableStylesheets = adoptions.flatMap(({ stylesheet, modes }) =>
    modes.includes('constructable') && stylesheet.sheet
      ? [stylesheet.sheet]
      : []
  );

  if (constructableStylesheets.length > 0 && 'adoptedStyleSheets' in root) {
    const sheetsToAdd = new Set(constructableStylesheets);
    const keysToReplace = new Set(
      constructableStylesheets
        .map((stylesheet) => getStylesheetKey(stylesheet))
        .filter((key): key is string => key !== undefined)
    );
    const signaturesToReplace = new Set(
      constructableStylesheets
        .map((stylesheet) => getStylesheetSignature(stylesheet))
        .filter((signature): signature is string => signature !== undefined)
    );
    const retained = root.adoptedStyleSheets.filter((stylesheet) => {
      if (sheetsToAdd.has(stylesheet)) {
        return false;
      }

      const stylesheetKey = getStylesheetKey(stylesheet);
      const stylesheetSignature = getStylesheetSignature(stylesheet);

      if (stylesheetKey !== undefined && keysToReplace.has(stylesheetKey)) {
        return false;
      }

      if (
        stylesheetSignature !== undefined &&
        signaturesToReplace.has(stylesheetSignature)
      ) {
        return false;
      }

      return true;
    });

    root.adoptedStyleSheets = [...retained, ...constructableStylesheets];
  }

  for (const { stylesheet, modes } of adoptions) {
    if (!modes.includes('fallback')) {
      continue;
    }

    appendFallbackStyle(root, stylesheet);
  }
}

export function releaseReusableStylesheets(
  root: Document | ShadowRoot,
  stylesheets: readonly ReusableStylesheet[]
): void {
  const releasedOwnerships = decrementStylesheetOwnership(root, stylesheets);

  if (releasedOwnerships.length === 0) {
    return;
  }

  if ('adoptedStyleSheets' in root) {
    const releasedConstructableStylesheets = releasedOwnerships.flatMap(
      ({ stylesheet, modes }) =>
        modes.has('constructable') && stylesheet.sheet ? [stylesheet.sheet] : []
    );

    if (releasedConstructableStylesheets.length > 0) {
      const releasedConstructableStylesheetSet = new Set(
        releasedConstructableStylesheets
      );

      root.adoptedStyleSheets = root.adoptedStyleSheets.filter(
        (stylesheet) => !releasedConstructableStylesheetSet.has(stylesheet)
      );
    }
  }

  const fallbackKeys = getFallbackKeys(root);
  const host = getFallbackStyleHost(root);

  for (const { stylesheet, modes } of releasedOwnerships) {
    if (!modes.has('fallback')) {
      continue;
    }

    getFallbackStyleElement(host, stylesheet.key)?.remove();
    fallbackKeys.delete(stylesheet.key);
  }
}

export function AdoptReusableStylesheets(props: {
  readonly stylesheets: readonly ReusableStylesheet[];
}): JSX.Element {
  if (typeof document === 'undefined') {
    return null;
  }

  const marker = document.createElement('span');
  marker.style.display = 'none';

  onMount(() => {
    const root = marker.getRootNode();

    if (isStyleRoot(root)) {
      adoptReusableStylesheets(root, props.stylesheets);
      onCleanup(() => releaseReusableStylesheets(root, props.stylesheets));
    }
  });

  return marker;
}

function getAdoptionModes(
  root: Document | ShadowRoot,
  stylesheet: ReusableStylesheet
): readonly AdoptionMode[] {
  if (stylesheet.sheet && 'adoptedStyleSheets' in root) {
    return ['constructable'];
  }

  return ['fallback'];
}

function trackReusableStylesheets(
  root: Document | ShadowRoot,
  adoptions: readonly {
    readonly stylesheet: ReusableStylesheet;
    readonly modes: readonly AdoptionMode[];
  }[]
): void {
  let ownershipByStylesheet = adoptedStylesheetOwnership.get(root);

  if (!ownershipByStylesheet) {
    ownershipByStylesheet = new Map();
    adoptedStylesheetOwnership.set(root, ownershipByStylesheet);
  }

  for (const { stylesheet, modes } of adoptions) {
    let ownership = ownershipByStylesheet.get(stylesheet);

    if (!ownership) {
      ownership = { count: 0, modes: new Set() };
      ownershipByStylesheet.set(stylesheet, ownership);
    }

    ownership.count += 1;

    for (const mode of modes) {
      ownership.modes.add(mode);
    }
  }
}

function decrementStylesheetOwnership(
  root: Document | ShadowRoot,
  stylesheets: readonly ReusableStylesheet[]
): ReleasedStylesheetOwnership[] {
  const ownershipByStylesheet = adoptedStylesheetOwnership.get(root);

  if (!ownershipByStylesheet) {
    return [];
  }

  const releasedOwnerships: ReleasedStylesheetOwnership[] = [];

  for (const stylesheet of stylesheets) {
    const ownership = ownershipByStylesheet.get(stylesheet);

    if (ownership === undefined) {
      continue;
    }

    if (ownership.count > 1) {
      ownership.count -= 1;
      continue;
    }

    ownershipByStylesheet.delete(stylesheet);
    releasedOwnerships.push({
      stylesheet,
      modes: ownership.modes,
    });
  }

  return releasedOwnerships;
}

function createConstructableStylesheet(
  cssText: string
): CSSStyleSheet | undefined {
  if (typeof CSSStyleSheet === 'undefined' || cssText.includes('@import')) {
    return undefined;
  }

  const stylesheet = new CSSStyleSheet();

  try {
    stylesheet.replaceSync(cssText);
  } catch {
    return undefined;
  }

  return stylesheet;
}

function markStylesheet(stylesheet: CSSStyleSheet, key: string): void {
  const signature = getStylesheetSignature(stylesheet);

  try {
    Object.defineProperty(stylesheet, reusableStylesheetKeyProperty, {
      configurable: true,
      value: key,
    });

    if (signature !== undefined) {
      Object.defineProperty(stylesheet, reusableStylesheetSignatureProperty, {
        configurable: true,
        value: signature,
      });
    }
  } catch {
    (stylesheet as KeyedCSSStyleSheet)[reusableStylesheetKeyProperty] = key;

    if (signature !== undefined) {
      (stylesheet as KeyedCSSStyleSheet)[reusableStylesheetSignatureProperty] =
        signature;
    }
  }
}

function getStylesheetKey(stylesheet: CSSStyleSheet): string | undefined {
  return (stylesheet as KeyedCSSStyleSheet)[reusableStylesheetKeyProperty];
}

function getStylesheetSignature(stylesheet: CSSStyleSheet): string | undefined {
  const markedSignature = (stylesheet as KeyedCSSStyleSheet)[
    reusableStylesheetSignatureProperty
  ];

  if (markedSignature !== undefined) {
    return markedSignature;
  }

  try {
    const rules = Array.from(stylesheet.cssRules);
    const rulesText = rules.map((rule) => rule.cssText).join('\n');

    return `${String(rules.length)}:${rulesText}`;
  } catch {
    return undefined;
  }
}

function appendFallbackStyle(
  root: Document | ShadowRoot,
  stylesheet: ReusableStylesheet
): void {
  const adoptedKeys = getFallbackKeys(root);
  const host = getFallbackStyleHost(root);
  const existingStyle = getFallbackStyleElement(host, stylesheet.key);

  if (existingStyle) {
    host.appendChild(existingStyle);
    adoptedKeys.add(stylesheet.key);
    return;
  }

  if (adoptedKeys.has(stylesheet.key)) {
    return;
  }

  const style = document.createElement('style');
  style.dataset['trimbleReusableStylesheet'] = stylesheet.key;
  style.textContent = stylesheet.cssText;
  host.appendChild(style);
  adoptedKeys.add(stylesheet.key);
}

function getFallbackStyleElement(
  host: Node,
  key: string
): HTMLStyleElement | undefined {
  if (
    !(
      host instanceof Document ||
      host instanceof ShadowRoot ||
      host instanceof Element
    )
  ) {
    return undefined;
  }

  return (
    Array.from(
      host.querySelectorAll<HTMLStyleElement>(
        'style[data-trimble-reusable-stylesheet]'
      )
    ).find((style) => style.dataset['trimbleReusableStylesheet'] === key) ??
    undefined
  );
}

function getFallbackKeys(root: Document | ShadowRoot): Set<string> {
  const existing = adoptedFallbackKeys.get(root);

  if (existing) {
    return existing;
  }

  const next = new Set<string>();
  adoptedFallbackKeys.set(root, next);
  return next;
}

function getFallbackStyleHost(root: Document | ShadowRoot): Node {
  if ('head' in root) {
    return root.head;
  }

  return root;
}

function isStyleRoot(root: Node): root is Document | ShadowRoot {
  return root instanceof Document || root instanceof ShadowRoot;
}
